import { useEffect, useRef, useState } from "react";
import cornellLogo from "@/assets/cornell-logo.jpg";

const EducationSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} id="education" className="py-20 bg-secondary/30">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">
          Education
        </h2>

        <div className="max-w-3xl mx-auto">
          <div
            className={`relative transition-all duration-400 ease-out ${
              isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
            }`}
          >
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary to-accent" />

            {/* Timeline node */}
            <div className="relative pl-24">
              <div className="absolute left-0 top-4 flex items-center gap-3">
                <img 
                  src={cornellLogo} 
                  alt="Cornell University logo"
                  className="w-14 h-14 rounded-lg object-contain bg-white p-1 flex-shrink-0"
                />
              </div>

              <div className="bg-card border-2 rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow duration-250 ease-out">
                <h3 className="text-2xl font-bold mb-2">Cornell University</h3>
                <p className="text-lg text-primary font-semibold mb-2">
                  B.S. in Computer and Information Science
                </p>
                <p className="text-muted-foreground mb-4">
                  Ithaca, NY • Expected May 2026
                </p>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">GPA:</span>
                    <span className="text-muted-foreground">3.8 / 4.0</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">Honors:</span>
                    <span className="text-muted-foreground">Dean's List (All Semesters)</span>
                  </div>
                  <div className="mt-4">
                    <span className="font-semibold block mb-2">Relevant Coursework:</span>
                    <div className="flex flex-wrap gap-2">
                      {["Functional Programming", "OOP & Data Structures", "Applied Machine Learning", "Text Mining", "Business Intelligence Systems", "Data Science"].map((course) => (
                        <span
                          key={course}
                          className="px-3 py-1.5 bg-secondary/50 text-secondary-foreground rounded-full text-sm font-medium
                                     hover:bg-gradient-to-r hover:from-primary hover:to-accent hover:text-white
                                     transition-colors duration-200 ease-out cursor-default"
                        >
                          {course}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EducationSection;
